import { useUiFromSpecContext } from '../EditorContextProvider'
import { useDrop } from 'react-dnd'
import clsx from 'clsx'
import { ALL_ITEM_TYPES } from '../Editor/dndTypes'
import { useIsCanvasElementDragging, useIsPaletteElementDragging } from '../Editor/Panels/AddInput'

/**
 * A drop zone rendered on the canvas between (or inside) sibling elements.
 *
 * Only accepts PALETTE_ELEMENT drags (new elements from the component palette).
 * Canvas reordering is handled exclusively via the Layer Panel's ChildDropper.
 *
 * The component is invisible when idle and only expands into a visible drop
 * indicator while the user is carrying a palette item.
 */
export const AddChildItem = ({ xpath, index }: any) => {
  const { editor, language } = useUiFromSpecContext()

  // Track both palette AND canvas element drags
  const isPaletteDragging = useIsPaletteElementDragging()
  const isCanvasDragging = useIsCanvasElementDragging()
  const isAnyDragging = isPaletteDragging || isCanvasDragging

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      // Accept both new palette elements and existing canvas elements
      accept: ALL_ITEM_TYPES,
      drop: (_item, monitor) => {
        if (monitor.didDrop()) return
        return { xpath, index, uiType: 'dropper', fromDropper: true }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [xpath, index]
  )

  const isActive = canDrop && isOver

  if (!editor) {
    return null
  }

  return (
    <div
      ref={drop}
      className={clsx(
        'relative transition-all duration-200 ease-in-out',
        isAnyDragging ? 'h-8 w-full my-1' : 'h-2 w-full',
      )}
      aria-label="Drop zone"
    >
      {/* Active-drop highlight */}
      <div
        className={clsx(
          'absolute inset-0 rounded-md transition-all duration-200 border-2 border-dashed flex items-center justify-center text-xs font-medium',
          isActive && 'opacity-100 visible border-blue-400 bg-blue-50 text-blue-600',
          !isActive && isAnyDragging && 'opacity-60 visible border-slate-300 bg-slate-50 text-slate-400',
          !isActive && !isAnyDragging && 'opacity-0 invisible border-transparent'
        )}
        data-testid="canvas-drop-zone"
      >
        {isActive && (
          <span>{language === 'en' ? 'Release to move here' : 'Lepaskan untuk memindahkan ke sini'}</span>
        )}
        {isAnyDragging && !isActive && (
          <span className="text-[10px]">+</span>
        )}
      </div>
    </div>
  )
}
